/**
 * Combined Cursor workspace dropdown: Find existing workspaces + Open generated workspace
 * Gates functionality when project has no git path configured.
 */

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Loader2, Code2, ChevronDown, FolderOpen, Info, Settings, SlidersHorizontal } from "lucide-react"
import { toast } from "sonner"
import { fetchWithAuth } from "@/lib/api"
import { buildOpenWorkspaceUri, launchCursorUri } from "@/lib/launch-cursor-uri"
import { testIds } from "@/lib/test-ids"
import {
  QUIET_OVERFLOW_MENU_COLLISION_PADDING,
  QUIET_OVERFLOW_MENU_CONTENT_CLASS,
} from "@/lib/menu-chrome"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface WorkspaceFile {
  path: string
  name: string
  relativePath: string
  size: number
  modified: string
  modifiedAgo: string
}

/** Known hopwork/clients clone names for the Greater Good client (multi-site). */
const GREATER_GOOD_SUB_REPOS = ["ggsc", "ggia", "greatergood", "html-greater"] as const

interface CursorWorkspaceDropdownProps {
  todoId?: string | null
  todoTitle?: string | null
  projectName?: string | null
  projectId?: string | null
  projectSlug?: string | null
  documentId?: string | null
  className?: string
  /** When true, render nothing until project git path is configured (todo header UX). */
  hideUnlessGitPathConfigured?: boolean
  /** When true, render as a `DropdownMenuSub` ("Cursor" submenu) for embedding inside another
   * already-open `DropdownMenu` (e.g. the document viewer's "..." overflow menu) instead of
   * owning its own top-level trigger button. The find/open/options actions and their state are
   * unchanged - only the outer Radix wrapper differs. */
  asSubmenu?: boolean
}

export function CursorWorkspaceDropdown({
  todoId,
  todoTitle,
  projectName,
  projectId,
  projectSlug,
  documentId,
  className,
  hideUnlessGitPathConfigured = false,
  asSubmenu = false,
}: CursorWorkspaceDropdownProps) {
  const [isFindSearching, setIsFindSearching] = useState(false)
  const [isOpenGenerating, setIsOpenGenerating] = useState(false)
  const [findDialogOpen, setFindDialogOpen] = useState(false)
  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false)
  const [optionsGitPath, setOptionsGitPath] = useState("")
  const [workspaces, setWorkspaces] = useState<WorkspaceFile[]>([])
  const [copied, setCopied] = useState<string | null>(null)
  const [hasGitPath, setHasGitPath] = useState<boolean | null>(null)
  const [dbGitPath, setDbGitPath] = useState<string | null>(null)
  /** Bumped when git config is saved so we re-fetch (slug/name alone do not change). */
  const [gitStatusNonce, setGitStatusNonce] = useState(0)

  const slug = projectSlug ?? projectName?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') ?? ''
  const isGreaterGoodClient =
    slug === "greater-good" ||
    (projectName != null && projectName.toLowerCase().includes("greater good"))

  useEffect(() => {
    const onSaved = (ev: Event) => {
      const pid = (ev as CustomEvent<{ projectId?: string }>).detail?.projectId
      if (pid != null && projectId != null && String(pid) === String(projectId)) {
        setGitStatusNonce((n) => n + 1)
      }
    }
    window.addEventListener('flownotes-basecamp-git-config-saved', onSaved as EventListener)
    return () => window.removeEventListener('flownotes-basecamp-git-config-saved', onSaved as EventListener)
  }, [projectId])

  // Fetch git status when projectSlug or projectName available (for gating)
  // Uses same lookup as find-existing and generate: projects + basecamp_projects
  useEffect(() => {
    if (!slug && !projectName) {
      setHasGitPath(null)
      setDbGitPath(null)
      return
    }
    let cancelled = false
    const params = new URLSearchParams()
    if (slug) params.set('projectSlug', slug)
    if (projectName) params.set('projectName', projectName)
    if (projectId) params.set('basecampProjectId', String(projectId))
    fetchWithAuth(`/api/workspace/git-status?${params}`)
      .then(async (r) => {
        const data = (await r.json().catch(() => ({}))) as {
          success?: boolean
          hasGitPath?: boolean
          localGitPath?: string | null
          error?: string
        }
        if (!r.ok) {
          if (!cancelled && r.status === 503) {
            toast.warning("Can't check git path yet", {
              description:
                "PostgreSQL didn't respond. Confirm Docker Postgres is up, restart the API if needed, then open this menu again.",
            })
          }
          if (!cancelled) {
            setHasGitPath(null)
            setDbGitPath(null)
          }
          return
        }
        if (!cancelled && data?.success) {
          setHasGitPath(Boolean(data.hasGitPath))
          setDbGitPath(data.localGitPath != null && data.localGitPath !== "" ? String(data.localGitPath) : null)
        } else {
          if (!cancelled) {
            setHasGitPath(null)
            setDbGitPath(null)
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasGitPath(null)
          setDbGitPath(null)
        }
      })
    return () => { cancelled = true }
  }, [slug, projectName, projectId, gitStatusNonce])

  const openOptionsDialog = () => {
    setOptionsGitPath(dbGitPath ?? "")
    setOptionsDialogOpen(true)
  }

  const handleFind = async () => {
    if (!hasGitPath) {
      toast.error("Git path required", {
        description: "Configure the project git path in Project Settings first."
      })
      return
    }
    if (!slug) {
      toast.error("Cannot find workspaces", { description: "Project slug is required" })
      return
    }
    setIsFindSearching(true)
    setFindDialogOpen(true)
    try {
      const response = await fetchWithAuth(`/api/workspace/find-existing?projectSlug=${encodeURIComponent(slug)}`)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      }
      const data = await response.json()
      if (!data.success) throw new Error(data.error || data.message || 'Failed to find workspaces')
      setWorkspaces(data.workspaces || [])
      if ((data.workspaces?.length ?? 0) === 0) {
        toast.info("No workspace files found", {
          description: `No .code-workspace files found in ${projectName || slug} repository`
        })
      }
    } catch (error) {
      console.error('[FindWorkspace] Error:', error)
      toast.error("Failed to find workspaces", {
        description: error instanceof Error ? error.message : 'Unknown error'
      })
      setWorkspaces([])
    } finally {
      setIsFindSearching(false)
    }
  }

  const handleOpenExisting = async (workspacePath: string) => {
    const command = `cursor -n "${workspacePath}"`
    // cursor:// in-browser replaces the active Cursor window; new window requires CLI -n
    await navigator.clipboard.writeText(command).catch(() => {})
    setCopied(workspacePath)
    setTimeout(() => setCopied(null), 2000)
    toast.success("Command copied — new Cursor window", {
      description:
        "Paste in Terminal: opens this workspace in a new window. Avoids replacing the Maater/Cursor window you have open.",
      duration: 8000
    })
    setFindDialogOpen(false)
  }

  /**
   * @param kind default = use DB git path; options = may override (required if DB has no path)
   */
  const generateAndCopy = async (kind: "default" | "options") => {
    if (kind === "default" && !hasGitPath) {
      toast.error("Git path required", {
        description: "Configure the project git path in Project Settings first."
      })
      return
    }
    const override = optionsGitPath.trim()
    if (kind === "options" && !hasGitPath && !override) {
      toast.error("Git path required", {
        description: "Enter the hopwork/clients folder name (e.g. ggsc) or configure Project Settings."
      })
      return
    }
    if (!todoId) {
      toast.error("Cannot open workspace", { description: "Todo ID is required" })
      return
    }
    if (!projectName && !slug) {
      toast.error("Cannot open workspace", { description: "Project information is required" })
      return
    }
    setIsOpenGenerating(true)
    try {
      const body: Record<string, string | undefined> = {
        todoId,
        todoTitle: todoTitle || undefined,
        projectName: projectName || undefined,
        projectSlug: slug || undefined,
        documentId: documentId || undefined,
        basecampProjectId: projectId || undefined,
      }
      if (kind === "options" && override) {
        body.gitPath = override
      }
      const response = await fetchWithAuth(`/api/workspace/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
      }
      const data = await response.json()
      if (!data.success) throw new Error(data.error || data.message || 'Failed to generate workspace')

      if (data.deliveryMode === 'client') {
        const uriParams = new URLSearchParams()
        uriParams.set('todoId', String(todoId))
        if (todoTitle) uriParams.set('todoTitle', todoTitle)
        if (projectName) uriParams.set('projectName', projectName)
        if (slug) uriParams.set('projectSlug', slug)
        if (documentId) uriParams.set('documentId', documentId)
        if (projectId) uriParams.set('basecampProjectId', String(projectId))
        if (kind === 'options' && override) uriParams.set('gitPath', override)
        if (data.folderPath) uriParams.set('folderPath', data.folderPath)

        const uri = buildOpenWorkspaceUri(uriParams)
        await navigator.clipboard.writeText(uri).catch(() => {})
        if (kind === 'options') setOptionsDialogOpen(false)

        launchCursorUri(uri)

        toast.success("Opening workspace in Cursor", {
          description:
            "Link copied. If Cursor did not open, run CMD+Shift+P: Maater: Open Workspace from Clipboard Link (extension 0.1.10+).",
          duration: 10000,
          action: {
            label: "Copy link again",
            onClick: async () => {
              await navigator.clipboard.writeText(uri)
              toast.info("Link copied", { description: uri })
            }
          }
        })
        return
      }

      const command = `cursor -n "${data.workspacePath}"`
      await navigator.clipboard.writeText(command).catch(() => {})
      if (kind === "options") setOptionsDialogOpen(false)
      toast.success("Command copied — new Cursor window", {
        description:
          "Paste in Terminal to open the generated workspace in a new Cursor window. (In-browser cursor:// would reuse your current window.)",
        duration: 8000,
        action: {
          label: "Copy again",
          onClick: async () => {
            await navigator.clipboard.writeText(command)
            toast.info("Command copied", { description: command })
          }
        }
      })
    } catch (error) {
      console.error('[OpenInCursor] Error:', error)
      toast.error("Failed to generate workspace", {
        description: error instanceof Error ? error.message : 'Unknown error',
        duration: 8000
      })
    } finally {
      setIsOpenGenerating(false)
    }
  }

  const isBusy = isFindSearching || isOpenGenerating
  const canShow = projectName || slug
  const needsGitConfig = hasGitPath === false
  const canUseCursor = hasGitPath !== false // allow when true or null (loading/unknown)
  const showOpenWithOptions = Boolean(todoId) && canShow

  const handleConfigureGit = () => {
    window.dispatchEvent(new CustomEvent('basecamp-project-configure-git', { detail: { projectId } }))
  }

  if (!canShow) return null
  if (hideUnlessGitPathConfigured && hasGitPath !== true) return null

  const menuItems = (
    <>
      {needsGitConfig && (
        <>
          <div className="px-2 py-1.5 text-xs text-amber-600 dark:text-amber-500 bg-amber-500/10 rounded">
            No git path configured. Find & Open require it.
          </div>
          <DropdownMenuItem onClick={(e) => { e.preventDefault(); handleConfigureGit(); }}>
            <Settings className="mr-2 h-4 w-4" />
            Configure Git Path
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      <DropdownMenuItem
        onClick={(e) => { e.preventDefault(); handleFind(); }}
        disabled={isFindSearching || !canUseCursor}
        title={!canUseCursor ? "Configure git path first" : "Search the project repository for existing .code-workspace files"}
      >
        <FolderOpen className="mr-2 h-4 w-4" />
        Find existing workspaces
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={(e) => { e.preventDefault(); void generateAndCopy("default"); }}
        disabled={isOpenGenerating || !todoId || !canUseCursor}
        title={!canUseCursor ? "Configure git path first" : "Generate a task-specific workspace and open it in Cursor"}
      >
        <Code2 className="mr-2 h-4 w-4" />
        Open workspace
      </DropdownMenuItem>
      {showOpenWithOptions && (
        <DropdownMenuItem
          onClick={(e) => { e.preventDefault(); openOptionsDialog(); }}
          disabled={isOpenGenerating}
          title="Set which hopwork/clients folder to add (e.g. ggsc vs greatergood)"
        >
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Open workspace with options…
        </DropdownMenuItem>
      )}
    </>
  )

  return (
    <>
      {asSubmenu ? (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger data-testid={testIds.basecamp.cursorWorkspaceDropdownTrigger}>
            {isBusy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Code2 className="mr-2 h-4 w-4" />
            )}
            Cursor
            {needsGitConfig && (
              <span title="Git path not configured" className="ml-1.5">
                <Info className="h-3.5 w-3.5 text-amber-500" />
              </span>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent
            className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
            collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
          >
            {menuItems}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={isBusy}
              className={`gap-1.5 h-7 px-2.5 text-xs ${className ?? ''} ${needsGitConfig ? 'border-amber-500/50' : ''}`}
              title={needsGitConfig ? "Git path not configured - configure in Project Settings" : "Cursor workspace options"}
              data-testid={testIds.basecamp.cursorWorkspaceDropdownTrigger}
            >
              {isBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Code2 className="h-3 w-3" />
              )}
              Cursor
              {needsGitConfig && (
                <span title="Git path not configured">
                  <Info className="h-3 w-3 text-amber-500" />
                </span>
              )}
              <ChevronDown className="h-3 w-3 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}
            collisionPadding={QUIET_OVERFLOW_MENU_COLLISION_PADDING}
          >
            {menuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Dialog open={optionsDialogOpen} onOpenChange={setOptionsDialogOpen}>
        <DialogContent
          className="max-w-md"
          data-testid={testIds.basecamp.cursorWorkspaceOptionsDialog}
        >
          <DialogHeader>
            <DialogTitle>Open workspace with options</DialogTitle>
            <DialogDescription>
              Override which local git repo folder to include (under <code className="text-xs">~/hopwork/clients/</code>
              ). Leave empty to use the project default from settings. For Greater Good, pick a site or type a folder name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {isGreaterGoodClient && (
              <div className="space-y-2">
                <Label htmlFor="cursor-ws-gg-preset">Site repo (quick pick)</Label>
                <Select
                  value={GREATER_GOOD_SUB_REPOS.includes(optionsGitPath as (typeof GREATER_GOOD_SUB_REPOS)[number]) ? optionsGitPath : ""}
                  onValueChange={(v) => { setOptionsGitPath(v) }}
                >
                  <SelectTrigger id="cursor-ws-gg-preset" className="w-full">
                    <SelectValue placeholder="Choose ggsc, ggia, greatergood, …" />
                  </SelectTrigger>
                  <SelectContent>
                    {GREATER_GOOD_SUB_REPOS.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="cursor-ws-git-path-override">Git path override</Label>
              <Input
                id="cursor-ws-git-path-override"
                data-testid={testIds.basecamp.cursorWorkspaceGitPathInput}
                placeholder="e.g. ggsc"
                value={optionsGitPath}
                onChange={(e) => setOptionsGitPath(e.target.value)}
                autoComplete="off"
              />
              {dbGitPath && (
                <p className="text-xs text-muted-foreground">Project default: {dbGitPath} (used when the field is empty.)</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOptionsDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => { void generateAndCopy("options") }}
              disabled={isOpenGenerating}
            >
              {isOpenGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Generate &amp; copy command
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={findDialogOpen} onOpenChange={setFindDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Existing Workspace Files</DialogTitle>
            <DialogDescription>
              Searches the project repo for .code-workspace files. Found {workspaces.length} in {projectName || slug}. Click Open to launch Cursor.
            </DialogDescription>
          </DialogHeader>
          {isFindSearching ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Searching repository...</span>
            </div>
          ) : workspaces.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No .code-workspace files found in this repository
            </div>
          ) : (
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-2">
                {workspaces.map((ws) => (
                  <div
                    key={ws.path}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{ws.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{ws.relativePath}</div>
                      <div className="text-xs text-muted-foreground mt-1">Modified {ws.modifiedAgo}</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenExisting(ws.path)}
                      className="ml-4 flex-shrink-0"
                    >
                      {copied === ws.path ? (
                        <>Copied!</>
                      ) : (
                        <>Open</>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
